import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/** Local Field helper — matches the one in Warehouses.jsx to keep visual parity. */
function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="label-caps mb-1">{label}</div>
      {children}
    </div>
  );
}

/**
 * MovementDialog — extracted from /pages/warehouse/Warehouses.jsx (iter47 refactor).
 * Handles IN/OUT/ADJUST stock movements for a single warehouse item and shows
 * the movement history table.
 */
export default function MovementDialog({ item, onClose, onReload }) {
  const [form, setForm] = useState({ type: "in", qty: 0, date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    if (item) {
      api.get(`/warehouse-items/${item.id}/movements`).then((r) => setMovements(r.data));
      setForm({ type: "in", qty: 0, date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
    }
  }, [item]);

  const submit = async () => {
    try {
      await api.post(`/warehouse-items/${item.id}/movements`, { ...form, item_id: item.id, qty: Number(form.qty) });
      toast.success("Movement recorded");
      onReload(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">{item.name} <span className="text-xs text-slate-500 font-mono">· {item.sku}</span></DialogTitle>
              <div className="text-xs text-slate-500">Current stock: <span className="font-mono-num font-semibold">{Number(item.stock).toFixed(2)} {item.unit}</span></div>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="mv-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in">IN</SelectItem><SelectItem value="out">OUT</SelectItem><SelectItem value="adjust">ADJUST</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Qty"><Input type="number" data-testid="mv-qty-input" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></Field>
              <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              <Field label="Reference"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="VPO-2001" /></Field>
              <Button onClick={submit} data-testid="save-movement-btn" className="rounded-sm bg-[#0F172A] hover:bg-slate-800 h-9">Record</Button>
            </div>
            <div className="mt-3">
              <div className="label-caps mb-1">History</div>
              {movements.length === 0 ? (
                <div className="text-xs text-slate-400 py-4 text-center">No movements yet.</div>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-sm">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50 text-xs">
                      <TableHead className="label-caps">Date</TableHead>
                      <TableHead className="label-caps">Type</TableHead>
                      <TableHead className="label-caps text-right">Qty</TableHead>
                      <TableHead className="label-caps text-right">Stock After</TableHead>
                      <TableHead className="label-caps">Reference</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{movements.map((m) => {
                      const typePill = (() => {
                        if (m.type === "in") return "bg-emerald-50 text-emerald-700";
                        if (m.type === "out") return "bg-red-50 text-red-700";
                        return "bg-slate-100 text-slate-700";
                      })();
                      return (
                      <TableRow key={m.id}>
                        <TableCell className="font-mono-num text-xs">{fmtDate(m.date)}</TableCell>
                        <TableCell><span className={`text-[10px] px-1 py-0.5 rounded-sm uppercase font-semibold ${typePill}`}>{m.type}</span></TableCell>
                        <TableCell className="text-right font-mono-num">{Number(m.qty).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-num font-semibold">{Number(m.stock_after).toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-slate-600">{m.reference || "—"}</TableCell>
                      </TableRow>
                      );
                    })}</TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
